import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnInit,
    output,
    ViewEncapsulation,
    inject,
    input,
    effect,
    computed,
} from '@angular/core';
import { IChecklistTimeIntervalData } from '@fi-shared/checklist/checklist-v2';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { IAuditOnDemandOption } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { FiChecklist2ScheduleFormatService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-format.service';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { AbstractControl, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { FiChecklistTimeIntervalType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import {
    FiChecklistEndTimeIntervalDataTypes,
    FiChecklistStartTimeIntervalDataTypes,
    FiChecklistTimeIntervalDataType,
} from '@fi-shared/checklist/schedule/fi-checklist-time-interval-data-type';
import { FiLocaleService } from '@fi-shared/fi-locale.service';
import { isDefined } from '@fi-shared/utils';
import { fiFormatTwoDigit } from '@fi-shared/utils/fi-format-two-digits';
import { FiChecklistScheduleType } from '@fi-shared/checklist/schedule/schedule-edit/fi-checklist-schedule-type';
import { FiChecklist2ScheduleDialogService } from '@fi-app/shared/checklist/schedule/services/fi-checklist2-schedule-dialog.service';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { ErrorStateMatcher } from '@angular/material/core';
import { FiDateAdapter } from '@fi-shared/fi-date-picker/fi-date-adapter';

class TimeIntervalTouchedErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null): boolean {
        return !!(control?.invalid && control.touched);
    }
}

@Component({
    selector: 'fi-checklist2-time-interval',
    templateUrl: './time-interval.component.html',
    styleUrls: ['./time-interval.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-time-interval' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiChecklist2TimeIntervalComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiChecklist2TimeIntervalComponent,
        },
        FiDateAdapter,
    ],
    standalone: false,
})
export class FiChecklist2TimeIntervalComponent
    extends ControlValueAccessorValidatorBase<IChecklistTimeIntervalData>
    implements OnInit
{
    public scheduleType = input.required<FiChecklistScheduleType>();
    public intervalType = input<FiChecklistTimeIntervalType>();
    public isStartPeriod = input<boolean>();
    public isStartTimeDisabled = input<boolean>();
    public disabled = input<boolean>();
    public isHighlightedTimeInput = input<boolean>();

    @Input() minTime: string;
    @Input() maxTime: string;
    @Input() minDayOfWeek: number;
    @Input() maxDayOfWeek: number;
    @Input() minMonth: number;
    @Input() maxMonth: number;
    @Input() maxDayOfMonth: number;
    @Input() minDayOfMonth: number;
    @Input() intervalStart: IChecklistTimeIntervalData;
    @Input() monthId: number;
    @Input() timeInputLabel: string = 'common.start_time';
    @Input() disabledIntervalDataTypes: FiChecklistTimeIntervalDataType[] = [];

    public recalculateIntervalEnd = output<void>();
    public dataTypeChange = output<void>();

    private fiChecklist2ScheduleIntervalService = inject(FiChecklist2ScheduleIntervalService);
    private fiChecklist2ScheduleFormatService = inject(FiChecklist2ScheduleFormatService);
    private fiChecklist2ScheduleDialogsService = inject(FiChecklist2ScheduleDialogService);
    private fiLocalService = inject(FiLocaleService);
    private fiDateAdapter = inject(FiDateAdapter);

    readonly availableDaysOfWeek: INameIdObj[] = this.fiLocalService.getDaysOfWeekWithIds();
    readonly availableMonths: INameIdObj[] = this.fiLocalService.getMonthsWithIds();
    selectedMonthDays: number[] = [];
    currentIntervalDataKey: string;
    protected readonly timeIntervalTouchedErrorStateMatcher = new TimeIntervalTouchedErrorStateMatcher();

    protected isMonthSelectShown = computed(() => {
        return this.fiChecklist2ScheduleIntervalService.isYearlyInterval(this.intervalType());
    });

    protected isMonthlyInterval = computed(() => {
        return this.fiChecklist2ScheduleIntervalService.isMonthlyInterval(this.intervalType());
    });

    protected isAuditOrOnDemand = computed(() => {
        return this.fiChecklist2ScheduleIntervalService.isAuditOrOnDemandInterval(this.intervalType());
    });

    protected isOneTimeSchedule = computed(() => {
        return this.fiChecklist2ScheduleIntervalService.isOneTimeSchedule(this.intervalType());
    });

    protected isTimeSettingsShown = computed(() => {
        return (
            !this.fiChecklist2ScheduleIntervalService.isAuditOrOnDemandInterval(this.intervalType()) &&
            !this.isOneTimeSchedule()
        );
    });

    protected monthTimeIntervalDataTypes = computed<INameIdObj[]>(() => {
        return this.isStartPeriod() ? FiChecklistStartTimeIntervalDataTypes : FiChecklistEndTimeIntervalDataTypes;
    });

    protected timePeriodSuffixIconClass = computed(() => {
        return this.isStartPeriod() ? 'green' : 'red';
    });

    protected isTimeInputDisabled = computed(() => {
        return this.isStartTimeDisabled() && this.isStartPeriod();
    });

    constructor(injector: Injector) {
        super(injector);
        effect(() => {
            const intervalType = this.intervalType();
            this.updateSelectedMonthDaysOnIntervalTypChange();
            this.setCurrentIntervalDataKey();
        });
    }

    public ngOnInit(): void {
        this.updateSelectedMonthDaysOnIntervalTypChange();
        this.setCurrentIntervalDataKey();
    }

    private updateSelectedMonthDaysOnIntervalTypChange(): void {
        if (this.fiChecklist2ScheduleIntervalService.isYearlyInterval(this.intervalType())) {
            this.setSelectedMonthDays(this.ngModel.Month);
        }
        if (this.isMonthlyInterval()) {
            this.setSelectedMonthDays(this.monthId);
        }
    }

    private setCurrentIntervalDataKey(): void {
        if (this.isAuditOrOnDemand()) {
            this.currentIntervalDataKey = Object.keys(this.ngModel).find(
                (key: string) => !isNullOrUndefined(this.ngModel[key]),
            );
        }
    }

    protected currentIntervalDataKeyChange(): void {
        this.ngModel[this.currentIntervalDataKey] = this.getMinAcceptableValueAuditOnDemandInput();

        Object.keys(this.ngModel).forEach((intervalDataKey: string) => {
            if (intervalDataKey !== this.currentIntervalDataKey) {
                delete this.ngModel[intervalDataKey];
            }
        });
        this.doPropagateChanges();
    }

    protected getMinAcceptableValueAuditOnDemandInput(): number {
        return this.currentIntervalDataKey === 'Minute' && this.scheduleType() !== FiChecklistScheduleType.OnDemand
            ? 30
            : 1;
    }

    protected onTimeInputChange(newTime: string): void {
        this.ngModel = this.fiChecklist2ScheduleFormatService.getIntervalDataOnTimePickerChange(this.ngModel, newTime);
        this.doPropagateChanges();
    }

    protected getHourMinuteInputData(): string {
        return this.fiChecklist2ScheduleFormatService.getHourMinuteInputData(this.ngModel);
    }

    private setSelectedMonthDays(selectedMonthId: number): void {
        const maxDaysInMonth: number = this.fiLocalService.getLastDayInMonth(selectedMonthId);
        this.selectedMonthDays = Array.from({ length: maxDaysInMonth }, (_, i) => i + 1);
        if (this.ngModel.Day > maxDaysInMonth) {
            this.ngModel.Day = undefined;
        }
    }

    protected onMonthSelect(selectedMonthId: number | undefined): void {
        this.setSelectedMonthDays(selectedMonthId);
        this.doPropagateChanges();
    }

    protected onDataTypeChange(): void {
        if (this.ngModel.DataType === FiChecklistTimeIntervalDataType.Day) {
            delete this.ngModel.DayOfWeek;
            if (!this.ngModel.hasOwnProperty('Day')) {
                this.ngModel.Day = undefined;
            }
        } else if (
            this.ngModel.DataType === FiChecklistTimeIntervalDataType.FirstWeekDay ||
            this.ngModel.DataType === FiChecklistTimeIntervalDataType.LastWeekDay
        ) {
            delete this.ngModel.Day;
            if (!this.ngModel.hasOwnProperty('DayOfWeek')) {
                this.ngModel.DayOfWeek = undefined;
            }
        } else if (this.ngModel.DataType === FiChecklistTimeIntervalDataType.LastDay) {
            delete this.ngModel.Day;
            delete this.ngModel.DayOfWeek;
        }
        this.doPropagateChanges();
        this.dataTypeChange.emit();
    }

    protected isDaySettingsShown(): boolean {
        return (
            (this.isMonthlyInterval() && this.ngModel.DataType === FiChecklistTimeIntervalDataType.Day) ||
            this.fiChecklist2ScheduleIntervalService.isYearlyInterval(this.intervalType())
        );
    }

    protected isDayOfWeekSettingsShown(): boolean {
        return (
            this.fiChecklist2ScheduleIntervalService.isWeeklyInterval(this.intervalType()) ||
            (this.isMonthlyInterval() &&
                (this.ngModel.DataType === FiChecklistTimeIntervalDataType.FirstWeekDay ||
                    this.ngModel.DataType === FiChecklistTimeIntervalDataType.LastWeekDay))
        );
    }

    protected isMonthDisabled(monthId: number): boolean {
        return this.isIntervalOptionDisabled(this.maxMonth, this.minMonth, monthId);
    }

    protected isDayOfMonthDisabled(day: number): boolean {
        return this.isIntervalOptionDisabled(this.maxDayOfMonth, this.minDayOfMonth, day);
    }

    protected isDayOfWeekDisabled(dayOfWeekId: number): boolean {
        return this.isIntervalOptionDisabled(this.maxDayOfWeek, this.minDayOfWeek, dayOfWeekId);
    }

    private isIntervalOptionDisabled(maxValue: number, minValue: number, currentValue: number): boolean {
        return this.isStartPeriod() ? maxValue && currentValue > maxValue : minValue && currentValue < minValue;
    }

    protected isTimeIntervalDataTypeDisabled(dataType: FiChecklistTimeIntervalDataType): boolean {
        return this.disabledIntervalDataTypes.includes(dataType);
    }

    protected getAuditOnDemandOptions(): ReadonlyArray<IAuditOnDemandOption> {
        return this.fiChecklist2ScheduleIntervalService.auditOnDemandOptions;
    }

    protected getDayTimeValue(): string {
        const date: Date = this.fiChecklist2ScheduleIntervalService.convertIntervalTimeDateToDate(this.ngModel);
        if (!this.fiDateAdapter.isValid(date)) {
            return '';
        }

        const formattedDate: string = this.fiDateAdapter.format(date, 'input');
        const time: string = this.fiChecklist2ScheduleFormatService.getHourMinuteInputData(this.ngModel);

        return `${formattedDate}, ${time}`;
    }

    protected openDayTimeDialog($event: FocusEvent, inputElement: HTMLInputElement): void {
        $event.preventDefault();

        if (this.isTimeInputDisabled()) {
            return;
        }

        const date: Date = this.fiChecklist2ScheduleIntervalService.convertIntervalTimeDateToDate(this.ngModel);
        const minDate: Date = this.isStartPeriod()
            ? new Date()
            : this.fiChecklist2ScheduleIntervalService.getDateWithGapByInterval(this.intervalStart);

        this.fiChecklist2ScheduleDialogsService
            .getTimePickerDialog(date, minDate)
            .onYesButtonClick((resultDate: Date) => {
                this.updateModelByDate(resultDate);
            })
            .afterClosed(() => setTimeout(() => inputElement.blur()));
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        return {
            required: Object.keys(this.ngModel).some((key) => !isDefined(this.ngModel[key])),
        };
    }

    private updateModelByDate(newDate: Date) {
        this.ngModel = this.fiChecklist2ScheduleIntervalService.convertDateToIntervalTimeData(newDate);
        this.doPropagateChanges();
        this.recalculateIntervalEnd.emit();
        this.cdr.markForCheck();
    }

    protected readonly fiFormatTwoDigit = fiFormatTwoDigit;
}
